import { CollageFamily, type CollageCellSpec, type CollageTemplate } from '@/content/collageTemplates'
import { getCollageTemplate } from '@/content/collageTemplates'
import {
  centeredPresetRectInside,
  largestRectForRatioInside,
  orientationCropBounds,
  type Rect,
  type Size,
} from '@/core/geometry'
import { NodeKind, TransformKind, type CollageNode, type CompositionNode, type ImageNode, type SourceId } from '@/core/document/types'
import { FrameStyleId, plainFrameInnerSize } from '@/tools/frame/params'

export function collageCellRect(
  template: CollageTemplate,
  cell: CollageCellSpec,
  canvasSize: Size,
  gap: number,
): Rect {
  const gapPixels = template.family === CollageFamily.Grid
    ? gap * Math.min(canvasSize.width, canvasSize.height)
    : 0
  const rect = {
    x: cell.rect.x * canvasSize.width + gapPixels / 2,
    y: cell.rect.y * canvasSize.height + gapPixels / 2,
    width: cell.rect.width * canvasSize.width - gapPixels,
    height: cell.rect.height * canvasSize.height - gapPixels,
  }
  if (rect.width <= 0 || rect.height <= 0) {
    throw new Error(`Collage template "${template.id}" gap leaves a cell without a positive size`)
  }
  return rect
}

function cropImageToSize(
  node: ImageNode,
  targetSize: Size,
  sources: ReadonlyMap<SourceId, Size>,
): ImageNode {
  const source = sources.get(node.sourceId)
  if (!source) throw new Error(`Cannot fit collage image: source "${node.sourceId}" is missing`)
  const bounds = orientationCropBounds(source, node.transforms[TransformKind.Orientation])
  const currentCrop = node.transforms[TransformKind.Crop]
  const ratio = { w: targetSize.width, h: targetSize.height }
  const rect = currentCrop
    ? centeredPresetRectInside(currentCrop.rect, bounds, ratio)
    : largestRectForRatioInside(bounds, ratio)
  return { ...node, transforms: { ...node.transforms, [TransformKind.Crop]: { rect } } }
}

function fitNodeToCell(
  node: CompositionNode,
  targetSize: Size,
  sources: ReadonlyMap<SourceId, Size>,
): CompositionNode {
  if (node.kind === NodeKind.Image) return cropImageToSize(node, targetSize, sources)
  if (node.kind === NodeKind.Frame
    && node.params.styleId === FrameStyleId.Plain
    && node.child.kind === NodeKind.Image) {
    return {
      ...node,
      child: cropImageToSize(node.child, plainFrameInnerSize(targetSize, node.params.borderMm), sources),
    }
  }
  return node
}

export function fitCollageCells(
  node: CollageNode,
  canvasSize: Size,
  sources: ReadonlyMap<SourceId, Size>,
): CollageNode {
  const template = getCollageTemplate(node.templateId)
  return {
    ...node,
    cells: node.cells.map((child, index) => {
      if (!child) return null
      const cell = template.cells[index]
      if (!cell) throw new Error(`Collage "${node.id}" is missing template cell ${index + 1}`)
      const rect = collageCellRect(template, cell, canvasSize, node.gap)
      return fitNodeToCell(child, { width: rect.width, height: rect.height }, sources)
    }),
  }
}
